// OPT-297: the ordered section lists behind the /settings and /admin shells.
// One module feeds both the route table and the shell column so they cannot drift.
export const SETTINGS_SECTIONS = [
  {
    path: "accounts",
    name: "Accounts",
    component: () => import("@/views/accounts"),
    meta: {
      title: "Accounts",
      requiresAuth: true,
      requiresAdmin: false,
      icon: "mdi-account-box-outline",
      color: "teal",
      rightDrawerForm: "AccountForm",
    },
  },
  {
    path: "health",
    name: "ConnectionHealth",
    component: () => import("@/views/settings/ConnectionHealth"),
    meta: {
      title: "Connection health",
      requiresAuth: true,
      requiresAdmin: false,
      icon: "mdi-heart-pulse",
      color: "red",
    },
  },
  {
    path: "shipping",
    name: "Shipping",
    component: () => import("@/views/settings/Shipping.vue"),
    meta: {
      title: "Shipping",
      requiresAuth: true,
      requiresAdmin: false,
      icon: "mdi-truck-outline",
      color: "teal",
    },
  },
  {
    // The cost schedule: every seller's. It replaced Settings -> Sales (the dashboard's flat default cost) and
    // the store-only Settings -> Consignment; both old paths redirect here (router/index.js).
    path: "cost-of-goods",
    name: "CostOfGoods",
    component: () => import("@/views/settings/CostOfGoods.vue"),
    meta: {
      title: "Cost of goods",
      requiresAuth: true,
      requiresAdmin: false,
      icon: "mdi-tag-text-outline",
      color: "teal",
    },
  },
  {
    // OPT-295: a Pro feature. meta.feature hides the chip without it, and PlanGate shows PlanLocked on a direct visit.
    path: "locations",
    name: "Locations",
    component: () => import("@/views/settings/Locations.vue"),
    meta: {
      title: "Locations",
      requiresAuth: true,
      requiresAdmin: false,
      feature: "locations",
      icon: "mdi-map-marker-outline",
      color: "teal",
    },
  },
  {
    path: "billing",
    name: "Billing",
    component: () => import("@/views/billing"),
    meta: {
      title: "Billing",
      requiresAuth: true,
      requiresAdmin: false,
      icon: "mdi-credit-card-outline",
      color: "green",
    },
  },
];

export const ADMIN_TOOLS = [
  {
    path: "brands",
    name: "BrandManagement",
    component: () => import("@/components/admin/BrandManager"),
    meta: {
      title: "Brand Management",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-tag-multiple-outline",
      color: "pink",
    },
  },
  {
    path: "features",
    name: "FeatureFlags",
    component: () => import("@/components/admin/FeatureFlagManager"),
    meta: {
      title: "Feature Flags",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-toggle-switch-outline",
      color: "indigo",
    },
  },
  {
    path: "ai-settings",
    name: "AISettings",
    component: () => import("@/components/admin/AISettings"),
    meta: {
      title: "AI Settings",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-robot-outline",
      color: "purple",
    },
  },
  {
    path: "mercari-proxies",
    name: "MercariProxies",
    component: () => import("@/components/admin/MercariProxies"),
    meta: {
      title: "Mercari Proxies",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-server-network",
      color: "deep-orange",
    },
  },
  {
    path: "error-analysis",
    name: "ErrorAnalysis",
    component: () => import("@/components/ErrorAnalysis"),
    meta: {
      title: "Error Analysis",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-bug-outline",
      color: "red",
    },
  },
  {
    path: "fix-listing-owners",
    name: "FixListingOwners",
    component: () => import("@/views/FixListingOwners"),
    meta: {
      title: "Fix Listing Owners",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-wrench-outline",
      color: "orange",
    },
  },
  {
    path: "diagnose-marketplace-auth",
    name: "DiagnoseMarketplaceAuth",
    component: () => import("@/views/DiagnoseMarketplaceAuth"),
    meta: {
      title: "Diagnose Marketplace Auth",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-magnify",
      color: "blue",
    },
  },
  {
    path: "cleanup-orphaned-listings",
    name: "CleanupOrphanedListings",
    component: () => import("@/views/CleanupOrphanedListings"),
    meta: {
      title: "Cleanup Orphaned Listings",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-broom",
      color: "red",
    },
  },
  {
    path: "cleanup-duplicate-medusa",
    name: "CleanupDuplicateMedusaListings",
    component: () => import("@/views/CleanupDuplicateMedusaListings"),
    meta: {
      title: "Cleanup Duplicate Medusa Listings",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-content-duplicate",
      color: "purple",
    },
  },
  {
    path: "cleanup-no-location",
    name: "CleanupNoLocationInventory",
    component: () => import("@/views/CleanupNoLocationInventory"),
    meta: {
      title: "Cleanup Inventory Without Location",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-map-marker-off-outline",
      color: "orange",
    },
  },
  {
    path: "channel-gaps",
    name: "ChannelGaps",
    component: () => import("@/views/ChannelGaps"),
    meta: {
      title: "Channel Gaps",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-storefront-outline",
      color: "purple",
    },
  },
  {
    // Was the Medusa-only version of the page above. Kept because the
    // route was never linked from the nav -- anyone who has it has it
    // bookmarked.
    path: "list-to-medusa",
    redirect: { name: "ChannelGaps" },
  },
  {
    path: "mark-missing",
    name: "MarkMissingInventory",
    component: () => import("@/views/MarkMissingInventory"),
    meta: {
      title: "Mark Missing Inventory",
      requiresAuth: true,
      requiresAdmin: true,
      icon: "mdi-help-circle-outline",
      color: "orange",
    },
  },
];

// Old /settings/<tool> paths keep working: each redirects to /admin/<tool>,
// carrying query and hash.
export function legacyAdminRedirects(tools) {
  return tools.map((t) => ({
    path: `/settings/${t.path}`,
    hidden: true,
    redirect: (to) => ({ path: `/admin/${t.path}`, query: to.query, hash: to.hash }),
  }));
}
